/**
 * Google OAuth Service
 * Handles Google authentication in the browser environment
 */

import { env } from '@/env/env';
import { AuthError, AuthErrorType, GoogleTokenPayload } from '../types';

/**
 * Google OAuth configuration interface
 */
interface GoogleAuthConfig {
    clientId: string;
    redirectUri?: string;
}

/**
 * Google OAuth service class
 */
export class GoogleAuthService {
    private readonly config: GoogleAuthConfig;
    private isInitialized = false;

    constructor(config: GoogleAuthConfig) {
        this.config = config;
    }

    /**
     * Initialize Google Sign-In
     */
    async initialize(): Promise<void> {
        if (typeof window === 'undefined') {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Google Sign-In can only be initialized in browser environment'
            );
        }

        if (this.isInitialized) {
            return;
        }

        await this.loadGoogleScript();
        await this.configureGoogleSignIn();
        this.isInitialized = true;
    }

    /**
     * Parse Google ID token payload (client-side only for display purposes)
     * Note: Server-side verification is still required for security
     */
    parseIdToken(idToken: string): GoogleTokenPayload {
        try {
            const parts = idToken.split('.');
            if (parts.length !== 3) {
                throw new AuthError(
                    AuthErrorType.INVALID_TOKEN,
                    'Invalid Google ID token format'
                );
            }

            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
            const payload = JSON.parse(atob(padded));

            if (!payload.sub || !payload.email) {
                throw new AuthError(
                    AuthErrorType.INVALID_TOKEN,
                    'Invalid Google ID token - missing required fields'
                );
            }

            return {
                iss: payload.iss || '',
                sub: payload.sub,
                email: payload.email,
                email_verified: payload.email_verified || false,
                name: payload.name || '',
                picture: payload.picture || '',
                given_name: payload.given_name || '',
                family_name: payload.family_name || '',
                iat: payload.iat || 0,
                exp: payload.exp || 0,
            };
        } catch (error) {
            if (error instanceof AuthError) {
                throw error;
            }

            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Failed to parse Google ID token',
                error
            );
        }
    }

    /**
     * Render Google Sign-In button
     */
    renderSignInButton(
        containerId: string,
        options: {
            theme?: 'outline' | 'filled_blue' | 'filled_black';
            size?: 'large' | 'medium' | 'small';
            text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
            shape?: 'rectangular' | 'pill' | 'circle' | 'square';
            logo_alignment?: 'left' | 'center';
            width?: number;
            locale?: string;
        } = {}
    ): void {
        if (!this.isInitialized || !window.google) {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Google Identity Services not initialized'
            );
        }

        const defaultOptions = {
            theme: 'outline' as const,
            size: 'large' as const,
            text: 'signin_with' as const,
            shape: 'rectangular' as const,
            logo_alignment: 'left' as const,
            width: 300,
        };

        const buttonOptions = { ...defaultOptions, ...options };

        try {
            const container = document.getElementById(containerId);
            if (!container) {
                throw new Error(`Container element with ID '${containerId}' not found`);
            }

            window.google.accounts.id.renderButton(container, buttonOptions);
        } catch (error) {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Failed to render Google Sign-In button',
                error
            );
        }
    }

    /**
     * Prompt Google Sign-In
     */
    prompt(callback?: (notification: any) => void): void {
        if (!this.isInitialized || !window.google) {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Google Identity Services not initialized'
            );
        }

        try {
            window.google.accounts.id.prompt(callback);
        } catch (error) {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Failed to prompt Google Sign-In',
                error
            );
        }
    }

    /**
     * Load Google Identity Services script
     */
    private async loadGoogleScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if script is already loaded
            if (document.getElementById('google-identity-script')) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.id = 'google-identity-script';
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;

            script.onload = () => resolve();
            script.onerror = () => {
                reject(new AuthError(
                    AuthErrorType.GOOGLE_AUTH_ERROR,
                    'Failed to load Google Identity Services script'
                ));
            };

            document.head.appendChild(script);
        });
    }

    /**
     * Configure Google Sign-In
     */
    private async configureGoogleSignIn(): Promise<void> {
        // Wait for Google Identity Services to be available
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds maximum wait

        while (!window.google && attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }

        if (!window.google) {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Google Identity Services failed to load'
            );
        }

        try {
            window.google.accounts.id.initialize({
                client_id: this.config.clientId,
                callback: this.handleGoogleResponse.bind(this),
                auto_select: false,
                cancel_on_tap_outside: true,
            });
        } catch (error) {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Failed to initialize Google Sign-In',
                error
            );
        }
    }

    /**
     * Handle Google Sign-In response
     * This method is meant to be overridden by components
     */
    private handleGoogleResponse(response: any): void {
        console.log('Google response received:', response);
    }
}

/**
 * Singleton Google Auth service instance
 */
let googleAuthService: GoogleAuthService | null = null;

/**
 * Get or create Google Auth service instance
 */
export function getGoogleAuthService(): GoogleAuthService {
    if (!googleAuthService) {
        const clientId = env('NEXT_PUBLIC_GOOGLE_CLIENT_ID');

        if (!clientId) {
            throw new AuthError(
                AuthErrorType.GOOGLE_AUTH_ERROR,
                'Google Client ID not configured. Please set NEXT_PUBLIC_GOOGLE_CLIENT_ID environment variable.'
            );
        }

        googleAuthService = new GoogleAuthService({
            clientId,
            redirectUri: typeof window !== 'undefined' ? window.location.origin : undefined,
        });
    }

    return googleAuthService;
}